/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import { Component, Input, EventEmitter, Output, OnInit, OnDestroy } from '@angular/core';
import { UserService } from '../../services/user.service';
import { XrxControlBaseComponent } from '../controls/xrx-control-base.component';
import { radioListType } from '../controls/xrx-radio.component';
import { OptionListOptions, Parameter } from '../../services/workflow.service';

@Component({
  selector: 'xrx-audio-convert',
  templateUrl: './xrx-audio-convert.component.html',
  styleUrls: ['./xrx-audio-convert.component.less'],
  providers: [{ provide: XrxControlBaseComponent, useExisting: XrxAudioConvertComponent }],
  standalone: false,
})

export class XrxAudioConvertComponent extends XrxControlBaseComponent implements OnInit, OnDestroy {

  @Input() required: boolean;
  @Input() label: string;
  @Input() value: string; // the audio configuration
  @Input() list: OptionListOptions[] = []; // language list
  @Input() isAutomated: boolean = false;
  @Input() serviceIndex: Number;  // the service setting page: single workflow = 0, combo workflow >= 0
  @Input() setting: Parameter;

  @Output() change = new EventEmitter<string>();
  @Output() validation = new EventEmitter<boolean>();

  languageOption: OptionListOptions;
  locationOption: OptionListOptions;
  genderOption: radioListType;
  genderOptions: radioListType[] = [
    {
      "isChecked": true,
      "value": "Female",
      "display": "SDE_FEMALE"
    },
    {
      "isChecked": false,
      "value": "Male",
      "display": "SDE_MALE"
    }
  ];

  config: AudioConfig = { language: 'en', locale: 'US', gender: 'Female' }
  isValid: boolean = true;
  isSavedConfig: boolean;
  userLanguage: string;
  userCountryCode: string;
  audioPlayer: any = null;
  audioSampleFile: string;
  bothGenderVoices: boolean = true;

  constructor(private userService: UserService) {
    super();
  }

  ngOnInit() {
    this.loadSavedConfiguration();
    this.validate();
  }

  ngOnDestroy() {
    this.updateConfig();
    this.playAudioSample(false);
  }

  validate() {
    this.isValid = true;
    this.setting.error = '';  // clear any previous error message
    this.validation.emit(this.isValid);
  }

  onLanguageSelected() {
    let location: string;
    if (this.languageOption.value === this.userLanguage) {
      // default to the user's country code for locale
      location = this.userCountryCode;
    }
    else {
      // default to the country with same language value (for English default to US)
      location = (this.languageOption.value === 'en') ? 'US' : (this.languageOption.value as string).substring(0, 2);
    }

    this.locationOption = this.languageOption.options?.find(x => (x.value as string).toLowerCase() === location.toLowerCase()) ?? this.languageOption.options?.[0]!;
    this.updateConfig();
  }

  onGenderSelected() {
    this.updateConfig();
  }

  onLocationSelected() {
    this.updateConfig();
  }

  loadSavedConfiguration() {
    this.config = JSON.parse(this.value);
    this.genderOption = (this.config.gender.toLowerCase() === "female") ? this.genderOptions[0] : this.genderOptions[1];
    this.userLanguage = this.userService.user?.language.substring(0, 2)!;
    this.userCountryCode = this.userService.user?.country_code!;

    let language: string, country: string;

    if (this.config.language === "") {
      // not saved workflow, use the user's preferred language and country code
      language = this.userLanguage;
      country = this.userCountryCode;
    }
    else {
      // user saved workflow language
      language = this.config.language;
      country = this.config.locale || ((this.config.language !== 'en') ? this.config.language : 'US');  // if locale not set default to same as language code, for English default to US
    }

    this.languageOption = this.list.find(x => x.value === language) ?? this.list.find(x => x.value === 'en')!;  // default to English if language not supported
    this.locationOption = this.languageOption.options?.find(x => (x.value as string).toLowerCase() === country.toLowerCase()) ?? this.languageOption.options?.[0]!;

    this.updateConfig();
  }

  updateConfig() {
    this.config.language = this.languageOption.value as string;
    this.config.gender = this.genderOption.value as string;
    this.config.locale = this.locationOption.value as string;
    this.setAudioSample();

    this.value = JSON.stringify(this.config);
    this.change.emit(this.value);
  }

  async setAudioSample() {
    // verify sample files exist to enable the Play button and Gender selection if both voices exist

    // special case for Chinese: strip off "-Hans" or "-Hant" for name of sample voice files
    const language = this.config.language.startsWith("zh")
      ? this.config.language.substring(0, 2)
      : this.config.language;

    const audioSampleFemale = `audioSamples/${language}-${this.config.locale}-Female.mp3`;
    const audioSampleMale = `audioSamples/${language}-${this.config.locale}-Male.mp3`;

    const [femaleExists, maleExists] = await Promise.all([
      this.audioFileExists(audioSampleFemale),
      this.audioFileExists(audioSampleMale),
    ]);

    this.bothGenderVoices = femaleExists && maleExists;

    if (this.bothGenderVoices) {
      this.audioSampleFile = this.config.gender === 'Female' ? audioSampleFemale : audioSampleMale;
    } else {
      this.audioSampleFile = femaleExists ? audioSampleFemale : maleExists ? audioSampleMale : '';
      this.genderOption = femaleExists ? this.genderOptions[0] : this.genderOptions[1];
    }
  }

  async audioFileExists(url: string): Promise<boolean> {
    try {
      const response = await fetch(url, { method: 'HEAD' });
      if (!response.ok) {
        return false;
      }
      // check the content-type since the server may return the default index.html instead of 404
      const contentType = response.headers.get('content-type');
      return contentType?.split(';')[0].trim() === 'audio/mpeg';
    } catch {
      return false;
    }
  }

  async playAudioSample(play: boolean = true) {
    if (!this.audioPlayer && play) {
      const audio = new Audio();
      audio.src = this.audioSampleFile;
      audio.load();
      audio.play()
        .then(() => {
          this.audioPlayer = audio;
          this.audioPlayer.addEventListener('ended', () => {
            this.audioPlayer = null;
          });
        })
        .catch(() => {
          this.audioPlayer = null;
        });
    }
    else if (this.audioPlayer) {
      this.audioPlayer.pause();
      this.audioPlayer.currentTime = 0;
      this.audioPlayer = null;
    }
  }
}
export interface AudioConfig {
  language: string;
  locale: string;
  gender: string;
}
